/**
 * BRAND PRODUCT DETAIL MODAL
 * 
 * Shows full details for a brand product including:
 * - Photo carousel with captions
 * - Clickable swatcher credit links
 * - Product info (name, description, price, status)
 * - Copy to Collection / Add to Wishlist buttons
 */

import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ExternalLink, Tag } from 'lucide-react';
import { AppIcons } from '../../constants/icons';

// Photo type that supports both string URLs and object format
interface ProductPhoto {
  url: string;
  caption?: string;
  creditName?: string;
  creditUrl?: string;
}

// Product type
interface BrandProduct {
  id: string;
  name: string;
  description?: string | null;
  colorDescription?: string | null;
  category: string;
  photos: (string | ProductPhoto)[];
  collectionId?: string | null;
  collectionName?: string | null;
  status: 'available' | 'coming_soon' | 'sold_out' | 'discontinued';
  price?: number | null;
  sku?: string | null;
  tags?: string[];
}

interface BrandProductDetailModalProps {
  product: BrandProduct;
  brandName: string;
  onClose: () => void;
  onCopyToCollection: () => void;
  onAddToWishlist: () => void;
}

// Helper to normalize photo to object format
const normalizePhoto = (photo: string | ProductPhoto): ProductPhoto => {
  if (typeof photo === 'string') {
    return { url: photo };
  }
  return photo;
};

export default function BrandProductDetailModal({
  product,
  brandName,
  onClose,
  onCopyToCollection,
  onAddToWishlist
}: BrandProductDetailModalProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Normalize all photos to object format
  const photos: ProductPhoto[] = (product.photos || []).map(normalizePhoto);
  const currentPhoto = photos[currentPhotoIndex] || null;
  const hasMultiplePhotos = photos.length > 1;

  // Navigation
  const goToPrevPhoto = () => {
    setCurrentPhotoIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNextPhoto = () => {
    setCurrentPhotoIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  // Status display
  const getStatusDisplay = () => {
    switch (product.status) {
      case 'available':
        return { label: 'Available', emoji: '✅', className: 'bg-green-100 text-green-700' };
      case 'coming_soon':
        return { label: 'Coming Soon', emoji: '🔜', className: 'bg-blue-100 text-blue-700' };
      case 'sold_out':
        return { label: 'Sold Out', emoji: '🚫', className: 'bg-red-100 text-red-700' };
      case 'discontinued':
        return { label: 'Discontinued', emoji: '⏹️', className: 'bg-gray-100 text-gray-700' };
      default:
        return null;
    }
  };

  const status = getStatusDisplay();

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row">
        {/* Left side - Photo */}
        <div className="md:w-1/2 bg-gray-100 relative">
          {/* Main photo */}
          {currentPhoto ? (
            <div className="relative">
              <img
                src={currentPhoto.url}
                alt={`${product.name} - Photo ${currentPhotoIndex + 1}`}
                className="w-full aspect-square object-cover"
              />
              
              {/* Photo credit overlay */}
              {currentPhoto.creditName && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                  {currentPhoto.creditUrl ? (
                    <a
                      href={currentPhoto.creditUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-white hover:text-purple-300 transition-colors"
                    >
                      📷 {currentPhoto.creditName}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span className="text-sm text-white/90">📷 {currentPhoto.creditName}</span>
                  )}
                </div>
              )}

              {/* Navigation arrows */}
              {hasMultiplePhotos && (
                <>
                  <button
                    onClick={goToPrevPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={goToNextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="w-full aspect-square flex items-center justify-center">
              <AppIcons.ViewAll className="w-16 h-16 text-gray-300" />
            </div>
          )}

          {/* Caption */}
          {currentPhoto?.caption && (
            <div className="px-4 py-2 bg-white border-t border-gray-200">
              <p className="text-sm text-gray-600 italic">{currentPhoto.caption}</p>
            </div>
          )}

          {/* Photo dots indicator */}
          {hasMultiplePhotos && (
            <div className="flex justify-center gap-1.5 py-3 bg-white border-t border-gray-200">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPhotoIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentPhotoIndex 
                      ? 'bg-purple-600' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Thumbnail strip for multiple photos */}
          {hasMultiplePhotos && (
            <div className="flex gap-2 p-3 bg-gray-50 border-t border-gray-200 overflow-x-auto">
              {photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPhotoIndex(index)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentPhotoIndex
                      ? 'border-purple-500 ring-2 ring-purple-200'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right side - Details */}
        <div className="md:w-1/2 flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-4 border-b border-gray-200">
            <div>
              <p className="text-sm text-purple-600 font-medium">{brandName}</p>
              <h2 className="text-xl font-bold text-gray-900 mt-0.5">{product.name}</h2>
              {product.collectionName && (
                <p className="text-sm text-gray-500 mt-1">
                  From: {product.collectionName}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content - scrollable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Status & Price */}
            <div className="flex items-center gap-3">
              {status && (
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${status.className}`}>
                  {status.emoji} {status.label}
                </span>
              )}
              {product.price !== undefined && product.price !== null && (
                <span className="text-lg font-bold text-gray-900">
                  ${product.price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Color description */}
            {product.colorDescription && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Color</h3>
                <p className="text-gray-600">{product.colorDescription}</p>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
                <p className="text-gray-600">{product.description}</p>
              </div>
            )}

            {/* Category */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Category</h3>
              <p className="text-gray-600">{product.category}</p>
            </div>

            {/* SKU */}
            {product.sku && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">SKU</h3>
                <p className="text-gray-500 text-sm font-mono">{product.sku}</p>
              </div>
            )}

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Tag className="w-4 h-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-purple-50 text-purple-700 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <button
              onClick={onCopyToCollection}
              disabled={product.status === 'discontinued'}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AppIcons.Add className="w-5 h-5" />
              Copy to My Collection
            </button>
            <button
              onClick={onAddToWishlist}
              disabled={product.status === 'discontinued'}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-100 text-amber-700 rounded-xl font-medium hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AppIcons.Wishlist className="w-5 h-5" />
              Add to Wishlist
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
